/**
 * Authentication Pinia store
 * @module store/authStore
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import * as authApi from '../api/auth';

/**
 * Authentication store definition
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null);
  const token = ref(localStorage.getItem('token') || '');
  const loading = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const isAdmin = computed(() => user.value?.role === 'admin');

  // Actions
  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<boolean>} Success indicator
   */
  const login = async (credentials) => {
    loading.value = true;
    try {
      const response = await authApi.login(credentials);
      const { token: newToken, user: userData } = response.data;

      // Save token to localStorage
      localStorage.setItem('token', newToken);
      token.value = newToken;
      user.value = userData;

      return true;
    } catch (error) {
      ElMessage.error(error.response?.data?.message || '登录失败');
      console.error('Login error:', error);
      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Logout user
   */
  const logout = () => {
    localStorage.removeItem('token');
    token.value = '';
    user.value = null;
  };

  /**
   * Get current user
   * @returns {Promise<boolean>} Success indicator
   */
  const fetchCurrentUser = async () => {
    if (!token.value) return false;

    loading.value = true;
    try {
      const response = await authApi.getCurrentUser();
      user.value = response.data.data;
      return true;
    } catch (error) {
      console.error('Error fetching current user:', error);
      if (error.response?.status === 401) {
        logout();
      }
      return false;
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    user,
    token,
    loading,

    // Getters
    isAuthenticated,
    isAdmin,

    // Actions
    login,
    logout,
    fetchCurrentUser,
  };
});
